import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CrudListToolbar from "../entity/CrudListToolbar";
import ListPaginationBar from "../ListPaginationBar";
import { getTableFreezeColsClassName, TableActionColumnHeader, TableViewport } from "../TableShell";
import { useEntityListChrome } from "../../shared/hooks/useEntityListChrome";
import { useListPagination } from "../../shared/hooks/useListPagination";
import {
  formatStockPercentCell,
  signedPercentClassName,
} from "../../shared/utils/stockPercentFormat";
import { getMessageForFetchError } from "../../shared/utils/apiError";
import { formatBatchSummaryMessage } from "../../shared/utils/batchSummaryMessage";
import { bulkSequentialDelete } from "../../shared/utils/bulkSequentialDelete";
import {
  formatSelectedCountSuffix,
  intersectSelectedIdsWithRows,
} from "../../shared/utils/entityListSelection";
import { downloadCsvTextFromString } from "../../shared/utils/csvFileHelpers";
import { buildStockBuyRecCsvExportText } from "../../shared/utils/stockBuyRecommendationCsv";
import { iconBtnStyle } from "../../shared/icons/IconLib";
import { formatStockCurrency } from "../../shared/utils/stockHoldingsTable";
import {
  STOCK_BUY_REC_PCT_COLUMNS,
  resolveBuyRecVisibleColumns,
  stockBuyRecColumnsFromIds,
} from "../../shared/utils/stockBuyRecommendationColumns";
import { stockRequestBody } from "../../shared/api/stockContext";
import { isBuyRecAggregateFilter } from "../../shared/utils/stockBuyRecommendationTabs";
import { deleteStockBuyRecommendationFilter, fetchStockBuyRecommendationFilters } from "../../shared/api/stockBuyRecommendationFilters";
import DataRefreshControl from "../DataRefreshControl";
import StockSummaryStatCards from "./StockSummaryStatCards";
import StockBuyRecommendationRenameFilterModal from "./StockBuyRecommendationRenameFilterModal";
import {
  countStocksWithUpcomingEarnings,
  filterStocksWithUpcomingEarnings,
} from "../../shared/utils/stockEarningsUpcoming";
import {
  countStocksWithPositiveEquity,
  filterStocksWithPositiveEquity,
} from "../../shared/utils/stockCurrentHolding";
import { summarizeStockListRows } from "../../shared/utils/stockSummary";
import { partitionBuyRecFiltersForRefresh, isBuyRecFilterRefreshStale, BUY_REC_REFRESH_MIN_INTERVAL_HOURS } from "../../shared/utils/stockBuyRecRefreshPolicy";
import {
  StockBuyRecRefreshConfirmMessage,
  buildBuyRecAllFreshAlertMessage,
  buildBuyRecSingleFreshAlertMessage,
} from "../../shared/utils/stockBuyRecRefreshConfirm";
import { apiFetch } from "../../shared/api/apiFetch";

const PCT_COLUMNS = STOCK_BUY_REC_PCT_COLUMNS;

const SORT_KEY_TYPES = {
  Symbol: "string",
  "Company Name": "string",
  "Last Price": "number",
  "Change (%)": "number",
  "52-Wk High Chg %": "number",
  "52-Wk Low Chg %": "number",
  "52-DMA Chg %": "number",
  "Market Cap": "number",
  "Earnings Date": "date",
  "P/E Ratio (TTM)": "number",
  "Forward P/E": "number",
  "Price/Sales": "number",
  "Short Ratio": "number",
  Equity: "number",
};

function parsePercentSortValue(raw) {
  if (raw == null || raw === "") return Number.NEGATIVE_INFINITY;
  const cleaned = String(raw).replace(/%/g, "").replace(/,/g, "").replace(/\+/g, "").trim();
  if (!cleaned || /^n\/a$/i.test(cleaned)) return Number.NEGATIVE_INFINITY;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
}

function parsePriceSortValue(raw) {
  if (raw == null || raw === "") return Number.NEGATIVE_INFINITY;
  const cleaned = String(raw).replace(/,/g, "").trim();
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
}

function parseMarketCapSortValue(raw) {
  if (raw == null || raw === "") return Number.NEGATIVE_INFINITY;
  const s = String(raw).trim().toUpperCase();
  if (!s || s === "N/A") return Number.NEGATIVE_INFINITY;
  const num = parseFloat(s.replace(/[^0-9.]/g, ""));
  if (!Number.isFinite(num)) return Number.NEGATIVE_INFINITY;
  if (s.includes("T")) return num * 1e12;
  if (s.includes("B")) return num * 1e9;
  if (s.includes("M")) return num * 1e6;
  if (s.includes("K")) return num * 1e3;
  return num;
}

function parseNumericSortValue(raw) {
  if (raw == null || raw === "") return Number.NEGATIVE_INFINITY;
  const cleaned = String(raw).replace(/,/g, "").trim();
  if (!cleaned || /^n\/a$/i.test(cleaned)) return Number.NEGATIVE_INFINITY;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : Number.NEGATIVE_INFINITY;
}

function getSortValue(row, key, type) {
  const raw =
    key === "Equity" ? (row.equity != null ? row.equity : row.Equity) : row[key];
  if (type === "date") {
    if (!raw) return -1;
    const t = new Date(String(raw).replace(/\//g, "-")).getTime();
    return Number.isFinite(t) ? t : -1;
  }
  if (type === "number") {
    if (PCT_COLUMNS.has(key)) return parsePercentSortValue(raw);
    if (key === "Last Price") return parsePriceSortValue(raw);
    if (key === "Market Cap") return parseMarketCapSortValue(raw);
    return parseNumericSortValue(raw);
  }
  return raw != null ? String(raw).toLowerCase() : "";
}

function sortBuyRecommendationRows(rows, sortConfig) {
  if (!sortConfig?.key) return rows;
  const type = SORT_KEY_TYPES[sortConfig.key] || "string";
  return [...rows].sort((a, b) => {
    const aVal = getSortValue(a, sortConfig.key, type);
    const bVal = getSortValue(b, sortConfig.key, type);
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });
}

function StockBuyRecommendation({
  stockSession,
  currentUserId,
  serverUrl,
  onError,
  setConfirmDialog,
  filterId,
  filterName,
  readOnly = false,
  onFiltersChanged,
  onCountsChanged,
  hasSavedFilters = true,
  savedFilters = [],
  earningsUpcomingFilter = false,
  onUpcomingEarningsClick,
  currentHoldingFilter = false,
  onCurrentHoldingClick,
  visibleColumnIds,
  onOpenColumnCustomizer,
  columnPrefsSaving = false,
}) {
  const accountId = stockSession?.account_id;
  const aggregate = isBuyRecAggregateFilter(filterId);
  const displayColumnIds = useMemo(
    () => resolveBuyRecVisibleColumns(visibleColumnIds),
    [visibleColumnIds]
  );
  const displayColumns = useMemo(
    () => stockBuyRecColumnsFromIds(displayColumnIds),
    [displayColumnIds]
  );
  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [deletingFilter, setDeletingFilter] = useState(false);
  const [renameFilterOpen, setRenameFilterOpen] = useState(false);
  const [hasLoadedList, setHasLoadedList] = useState(false);
  const [fetchedAt, setFetchedAt] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [totalSymbolsScraped, setTotalSymbolsScraped] = useState(null);

  const {
    selectedIds,
    setSelectedIds,
    deleting,
    setDeleting,
    searchTerm,
    setSearchTerm,
    sortConfig,
    setSortConfig,
    handleSort,
    handleSelectAll,
    handleSelectOne,
  } = useEntityListChrome({ initialSort: { key: "Symbol", direction: "asc" } });

  const applyListPayload = useCallback((data) => {
    setRows(Array.isArray(data.rows) ? data.rows : []);
    setFetchedAt(data.fetchedAt || "");
    setSourceUrl(data.sourceUrl || "");
    setTotalSymbolsScraped(data.totalSymbolsScraped ?? null);
  }, []);

  const listRequestBody = useMemo(() => {
    if (aggregate) {
      return stockRequestBody(stockSession, { aggregate: true, filterId: "all" });
    }
    return stockRequestBody(stockSession, { filterId });
  }, [stockSession, aggregate, filterId]);

  const onCountsChangedRef = useRef(onCountsChanged);
  onCountsChangedRef.current = onCountsChanged;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const loadScopeKey = aggregate ? "all" : filterId;
  const listInflightRef = useRef(null);
  const listRequestBodyRef = useRef(listRequestBody);
  listRequestBodyRef.current = listRequestBody;

  const fetchListForScope = useCallback(
    async (scopeKey) => {
      if (!accountId) return;
      if (!aggregate && !filterId) return;
      if (listInflightRef.current === scopeKey) return;

      listInflightRef.current = scopeKey;
      setLoadingList(true);
      try {
        const resp = await apiFetch(`${serverUrl}/listStockBuyRecommendations`, {
          method: "POST",
          body: JSON.stringify(listRequestBodyRef.current),
        });
        const data = await resp.json().catch(() => ({}));
        if (listInflightRef.current !== scopeKey) return;
        if (!resp.ok) {
          throw new Error(data.message || data.error || "Failed to load buy recommendations");
        }
        applyListPayload(data);
        setHasLoadedList(true);
        if (!aggregate) {
          const count = data.totalMatched ?? 0;
          onCountsChangedRef.current?.({ filterId, count });
        }
      } catch (err) {
        if (listInflightRef.current !== scopeKey) return;
        onErrorRef.current(getMessageForFetchError(err));
        setRows([]);
        setHasLoadedList(true);
      } finally {
        if (listInflightRef.current === scopeKey) {
          listInflightRef.current = null;
          setLoadingList(false);
        }
      }
    },
    [accountId, aggregate, filterId, serverUrl, applyListPayload]
  );

  useEffect(() => {
    setSelectedIds(new Set());
    fetchListForScope(loadScopeKey);
    return () => {
      listInflightRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch only when scope or account changes
  }, [loadScopeKey, accountId]);

  const pollYahooRefreshJob = useCallback(
    async (jobId) => {
      const pollBody = stockRequestBody(stockSession, { jobId });
      const deadline = Date.now() + 5 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const pollResp = await apiFetch(`${serverUrl}/getStockBuyRecommendationRefreshStatus`, {
          method: "POST",
          body: JSON.stringify(pollBody),
        });
        const pollData = await pollResp.json().catch(() => ({}));
        if (pollData.status === "pending") continue;
        if (!pollResp.ok || pollData.status === "error") {
          const detail =
            pollData.message ||
            pollData.error ||
            (pollData.result && (pollData.result.message || pollData.result.error)) ||
            (pollResp.status ? `Server returned HTTP ${pollResp.status}` : "");
          throw new Error(detail || "Yahoo refresh failed");
        }
        return pollData;
      }
      throw new Error(
        "Yahoo refresh is still running on the server. Wait a minute, then reload this tab."
      );
    },
    [serverUrl, stockSession]
  );

  const runRefreshFromYahoo = useCallback(async () => {
    if (!accountId) return;
    if (!currentUserId) {
      onError("User session is required to refresh buy recommendations.");
      return;
    }
    setRefreshing(true);
    onError("");
    try {
      const body = aggregate
        ? stockRequestBody(stockSession, { refreshAll: true, created_by: currentUserId })
        : stockRequestBody(stockSession, { filterId, created_by: currentUserId });

      const resp = await apiFetch(`${serverUrl}/refreshStockBuyRecommendations`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      let data = await resp.json().catch(() => ({}));
      if (resp.status === 202 && data.async && data.jobId) {
        data = await pollYahooRefreshJob(data.jobId);
      } else if (!resp.ok) {
        const serverMsg = data.message || data.error;
        const statusHint =
          resp.status === 503 || resp.status === 504
            ? " The server may have timed out (Heroku allows ~30s per request)."
            : resp.status
              ? ` (HTTP ${resp.status})`
              : "";
        throw new Error(
          serverMsg || `Failed to refresh buy recommendations${statusHint}`
        );
      }
      if (data.skipped && data.message) {
        setConfirmDialog({
          open: true,
          title: "Refresh buy lists",
          mode: "alert",
          message: data.message,
          onConfirm: null,
        });
      }
      if (Array.isArray(data.refreshErrors) && data.refreshErrors.length > 0) {
        onError(data.refreshErrors.join(" "));
      }
      applyListPayload(data);
      setSelectedIds(new Set());
      setHasLoadedList(true);
      if (onFiltersChanged) await onFiltersChanged();
      else if (onCountsChanged) {
        onCountsChanged({ filterId: aggregate ? "all" : filterId, count: data.totalMatched ?? 0 });
      }
    } catch (err) {
      onError(getMessageForFetchError(err));
    } finally {
      setRefreshing(false);
    }
  }, [
    accountId,
    aggregate,
    filterId,
    stockSession,
    currentUserId,
    serverUrl,
    onError,
    applyListPayload,
    setSelectedIds,
    onFiltersChanged,
    onCountsChanged,
    pollYahooRefreshJob,
    setConfirmDialog,
  ]);

  const confirmRefreshFromYahoo = useCallback(async () => {
    if (!accountId) return;
    if (!currentUserId) {
      onError("User session is required to refresh buy recommendations.");
      return;
    }

    if (aggregate) {
      let filtersForPartition = savedFilters;
      try {
        const fresh = await fetchStockBuyRecommendationFilters(serverUrl, stockSession);
        filtersForPartition = fresh.savedFilters;
        if (onFiltersChanged) await onFiltersChanged();
      } catch (err) {
        onError(getMessageForFetchError(err, "Could not load buy list refresh status."));
        return;
      }

      const { toRefresh, skipped } = partitionBuyRecFiltersForRefresh(filtersForPartition);
      if (!toRefresh.length) {
        setConfirmDialog({
          open: true,
          title: "Refresh buy lists",
          mode: "alert",
          message: buildBuyRecAllFreshAlertMessage(skipped),
          onConfirm: null,
        });
        return;
      }
      setConfirmDialog({
        open: true,
        title: "Refresh buy lists",
        mode: "confirm",
        confirmLabel: "Refresh",
        contentMaxWidth: 480,
        message: <StockBuyRecRefreshConfirmMessage toRefresh={toRefresh} skipped={skipped} />,
        onConfirm: () => runRefreshFromYahoo(),
      });
      return;
    }

    let activeFilter = savedFilters.find((f) => f.id === filterId);
    try {
      const fresh = await fetchStockBuyRecommendationFilters(serverUrl, stockSession);
      activeFilter = fresh.savedFilters.find((f) => f.id === filterId) || activeFilter;
      if (onFiltersChanged) await onFiltersChanged();
    } catch (err) {
      onError(getMessageForFetchError(err, "Could not load buy list refresh status."));
      return;
    }

    const yahooLastRefreshedAt =
      (fetchedAt && String(fetchedAt).trim()) || activeFilter?.yahooLastRefreshedAt || null;
    if (!isBuyRecFilterRefreshStale(yahooLastRefreshedAt)) {
      setConfirmDialog({
        open: true,
        title: "Refresh buy list",
        mode: "alert",
        message: buildBuyRecSingleFreshAlertMessage(filterName, yahooLastRefreshedAt),
        onConfirm: null,
      });
      return;
    }

    runRefreshFromYahoo();
  }, [
    accountId,
    currentUserId,
    aggregate,
    savedFilters,
    filterId,
    filterName,
    fetchedAt,
    serverUrl,
    stockSession,
    onError,
    setConfirmDialog,
    runRefreshFromYahoo,
    onFiltersChanged,
  ]);

  const searchFilteredRows = useMemo(() => {
    const searchLower = (searchTerm || "").toLowerCase().trim();
    if (!searchLower) return rows;
    return rows.filter((row) => {
      const hay = displayColumnIds
        .map((col) => row[col])
        .map((v) => (v != null ? String(v).toLowerCase() : ""))
        .join(" ");
      return hay.includes(searchLower);
    });
  }, [rows, searchTerm, displayColumnIds]);

  const upcomingEarningsCount = useMemo(
    () => countStocksWithUpcomingEarnings(searchFilteredRows),
    [searchFilteredRows]
  );

  const currentHoldingCount = useMemo(
    () => countStocksWithPositiveEquity(searchFilteredRows),
    [searchFilteredRows]
  );

  const currentHoldingFilteredRows = useMemo(() => {
    if (!currentHoldingFilter) return searchFilteredRows;
    return filterStocksWithPositiveEquity(searchFilteredRows);
  }, [searchFilteredRows, currentHoldingFilter]);

  const earningsFilteredRows = useMemo(() => {
    if (!earningsUpcomingFilter) return currentHoldingFilteredRows;
    return filterStocksWithUpcomingEarnings(currentHoldingFilteredRows);
  }, [currentHoldingFilteredRows, earningsUpcomingFilter]);

  useEffect(() => {
    if (!earningsUpcomingFilter) return;
    setSortConfig({ key: "Earnings Date", direction: "asc" });
  }, [earningsUpcomingFilter, setSortConfig]);

  const sortedRows = useMemo(
    () => sortBuyRecommendationRows(earningsFilteredRows, sortConfig),
    [earningsFilteredRows, sortConfig]
  );

  const {
    pageItems: paginatedRows,
    paginationBarProps,
    getSelectAllCheckboxProps,
  } = useListPagination(sortedRows, [
    rows.length,
    searchTerm,
    earningsUpcomingFilter,
    currentHoldingFilter,
    sortConfig.key,
    sortConfig.direction,
  ]);

  const listSummary = useMemo(() => summarizeStockListRows(earningsFilteredRows), [earningsFilteredRows]);

  function getSelectedVisibleIds() {
    return intersectSelectedIdsWithRows(selectedIds, sortedRows);
  }

  function exportSelectedCsv() {
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Export",
        message: "Select at least one stock to export.",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setExporting(true);
    try {
      const idSet = selectedVisibleIds;
      const selected = sortedRows
        .filter((row) => row && idSet.has(row._id))
        .sort((a, b) =>
          String(a?.Symbol ?? "").localeCompare(String(b?.Symbol ?? ""), undefined, { sensitivity: "base" })
        );
      if (selected.length === 0) {
        setConfirmDialog({
          open: true,
          title: "Export",
          message: "No matching stocks found for the current selection.",
          mode: "alert",
          onConfirm: null,
        });
        return;
      }
      const csv = buildStockBuyRecCsvExportText(selected, displayColumnIds);
      const datePart = new Date().toISOString().slice(0, 10);
      const slug = aggregate ? "all" : (filterName || filterId || "watchlist").replace(/\s+/g, "-");
      downloadCsvTextFromString(`buy-recommendations-${slug}-${datePart}.csv`, csv);
      onError("");
    } catch (err) {
      onError(getMessageForFetchError(err, "Error exporting CSV."));
    } finally {
      setExporting(false);
    }
  }

  function deleteSelected() {
    if (readOnly) return;
    const selectedVisibleIds = getSelectedVisibleIds();
    if (selectedVisibleIds.size === 0) {
      setConfirmDialog({
        open: true,
        title: "Delete buy recommendations",
        message: "Please select at least one record to delete",
        mode: "alert",
        onConfirm: null,
      });
      return;
    }
    setConfirmDialog({
      open: true,
      title: "Delete buy recommendations",
      message: `Are you sure you want to delete ${selectedVisibleIds.size} record(s)?`,
      onConfirm: async () => {
        setDeleting(true);
        const idsToDelete = Array.from(selectedVisibleIds);
        const { successCount, errorCount, errors } = await bulkSequentialDelete(
          idsToDelete,
          async (_id) => {
            const response = await apiFetch(`${serverUrl}/deleteStockBuyRecommendation`, {
              method: "POST",
              body: JSON.stringify(stockRequestBody(stockSession, { _id, filterId: aggregate ? undefined : filterId })),
            });
            const data = await response.text();
            return { ok: response.ok, body: data };
          },
          getMessageForFetchError
        );
        setConfirmDialog({
          open: true,
          title: "Delete completed",
          message: formatBatchSummaryMessage("Delete completed!", successCount, errorCount, errors),
          onConfirm: null,
          mode: "alert",
        });
        setSelectedIds(new Set());
        await fetchListForScope(loadScopeKey);
        if (onFiltersChanged) await onFiltersChanged();
        setDeleting(false);
      },
    });
  }

  function confirmDeleteFilter() {
    if (aggregate || readOnly || !filterId) return;
    setConfirmDialog({
      open: true,
      title: "Delete watchlist",
      mode: "confirm",
      message: `Delete watchlist "${filterName || filterId}" and all saved stocks for it?`,
      onConfirm: async () => {
        setDeletingFilter(true);
        onError("");
        try {
          await deleteStockBuyRecommendationFilter(serverUrl, stockSession, filterId);
          if (onFiltersChanged) await onFiltersChanged({ deletedFilterId: filterId });
        } catch (err) {
          onError(getMessageForFetchError(err));
        } finally {
          setDeletingFilter(false);
        }
      },
    });
  }

  const freezeClass = getTableFreezeColsClassName(2, 1);
  const showTable = hasLoadedList && sortedRows.length > 0;
  const busy = loadingList || refreshing;
  const showEmptyState = hasLoadedList && !loadingList && sortedRows.length === 0;
  const canDeleteRows = !readOnly && !aggregate;
  const selectedVisibleIds = intersectSelectedIdsWithRows(selectedIds, sortedRows);
  const selectedVisibleCount = selectedVisibleIds.size;
  const selectedCountSuffix = formatSelectedCountSuffix(selectedVisibleCount);

  const filterHint = aggregate
    ? "Read-only roll-up across all saved watchlists (unique symbols)."
    : filterName
      ? `Watchlist: ${filterName}`
      : "Saved watchlist";

  return (
    <TableViewport>
      <StockBuyRecommendationRenameFilterModal
        open={renameFilterOpen}
        onClose={() => setRenameFilterOpen(false)}
        serverUrl={serverUrl}
        stockSession={stockSession}
        filterId={filterId}
        initialName={filterName}
        onError={onError}
        onSaved={async () => {
          if (onFiltersChanged) await onFiltersChanged();
        }}
      />
      <CrudListToolbar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search symbol, company name, price, % change, market cap, earnings…"
        onClearSearch={() => setSearchTerm("")}
        showCsvImport={false}
        showNew={false}
        extraActions={
          <>
            {sourceUrl ? (
              <a
                href={sourceUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-md btn-secondary"
              >
                Yahoo view
              </a>
            ) : null}
            {!aggregate && !readOnly ? (
              <button
                type="button"
                className="btn btn-md btn-secondary"
                disabled={deletingFilter}
                onClick={() => setRenameFilterOpen(true)}
              >
                Rename watchlist
              </button>
            ) : null}
            {!aggregate && !readOnly ? (
              <button
                type="button"
                className="btn btn-md btn-secondary"
                disabled={deletingFilter}
                onClick={() => confirmDeleteFilter()}
              >
                {deletingFilter ? "Deleting…" : "Delete watchlist"}
              </button>
            ) : null}
            <button
              type="button"
              className="btn btn-md btn-primary-outline"
              disabled={selectedVisibleCount === 0 || exporting}
              onClick={() => exportSelectedCsv()}
            >
              {exporting ? "Exporting…" : `Export CSV${selectedCountSuffix}`}
            </button>
            <DataRefreshControl
              source="Yahoo"
              lastRefreshedAt={fetchedAt || null}
              refreshing={refreshing}
              disabled={busy || (aggregate && !hasSavedFilters)}
              onRefresh={() => confirmRefreshFromYahoo()}
              title={
                aggregate
                  ? `Refresh stale buy watchlists from Yahoo Finance (skips lists updated within ${BUY_REC_REFRESH_MIN_INTERVAL_HOURS} hours)`
                  : `Refresh this watchlist from Yahoo Finance (skips if updated within ${BUY_REC_REFRESH_MIN_INTERVAL_HOURS} hours)`
              }
            />
          </>
        }
        onDeleteSelected={deleteSelected}
        deleteDisabled={!canDeleteRows || selectedVisibleCount === 0 || deleting}
        deleteCountSuffix={selectedCountSuffix}
        showViewToggle={false}
        showDelete={canDeleteRows}
      />

      <div className="panel" style={{ marginBottom: 8 }}>
        <p className="panel-hint" style={{ margin: 0 }}>
          {filterHint}. Criteria: 52-Wk High Chg % &lt; −30% and 52-DMA Chg % &gt; 0 (from Yahoo Finance portfolio
          view).
          {totalSymbolsScraped != null ? ` • Scanned ${totalSymbolsScraped} symbols` : ""}
        </p>
      </div>

      {currentHoldingFilter ? (
        <div className="panel" style={{ marginBottom: 8 }}>
          <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <span className="panel-hint-inline">
              Drilldown filter active: Current holding (equity &gt; 0) • {earningsFilteredRows.length} stock
              {earningsFilteredRows.length === 1 ? "" : "s"}
            </span>
            {onCurrentHoldingClick ? (
              <button type="button" className="btn btn-sm btn-secondary" onClick={onCurrentHoldingClick}>
                Clear drilldown
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {earningsUpcomingFilter ? (
        <div className="panel" style={{ marginBottom: 8 }}>
          <div className="panel-row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <span className="panel-hint-inline">
              Drilldown filter active: Upcoming earnings (30 days) • {earningsFilteredRows.length} stock
              {earningsFilteredRows.length === 1 ? "" : "s"}
            </span>
            {onUpcomingEarningsClick ? (
              <button type="button" className="btn btn-sm btn-secondary" onClick={onUpcomingEarningsClick}>
                Clear drilldown
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {hasLoadedList && !loadingList ? (
        <StockSummaryStatCards
          stockCount={listSummary.stockCount}
          showTotalInvestment={false}
          currentHoldingCount={currentHoldingCount}
          currentHoldingActive={currentHoldingFilter}
          onCurrentHoldingClick={onCurrentHoldingClick}
          upcomingEarningsCount={upcomingEarningsCount}
          upcomingEarningsActive={earningsUpcomingFilter}
          onUpcomingEarningsClick={onUpcomingEarningsClick}
        />
      ) : null}

      {loadingList ? <p className="panel-hint">Loading saved buy recommendations…</p> : null}

      {showEmptyState ? (
        <p className="panel-hint">
          {aggregate
            ? "No stocks in the roll-up yet. Add a saved watchlist or refresh individual watchlists."
            : "No saved buy recommendations for this watchlist. Use the Yahoo refresh control above to fetch from Yahoo Finance."}
        </p>
      ) : null}

      {showTable ? (
        <>
          <ListPaginationBar {...paginationBarProps} />
          <div className="table-scroll">
            <div className={`table-wrapper table-wrapper-stock-mgmt table-wrapper--sticky-header ${freezeClass}`.trim()}>
              <table>
                <thead>
                  <tr>
                    <th className="text-center">
                      <input
                        type="checkbox"
                        {...getSelectAllCheckboxProps(handleSelectAll, selectedIds)}
                        disabled={!canDeleteRows}
                      />
                    </th>
                    {displayColumns.map((col) => (
                      <th
                        key={col.id}
                        className={`sortable${col.id === "Equity" ? " table-cell-numeric" : ""}`}
                        onClick={() => handleSort(col.id)}
                      >
                        <span className="table-th-label">{col.label}</span>
                      </th>
                    ))}
                    <TableActionColumnHeader
                      onCustomizeColumns={onOpenColumnCustomizer || undefined}
                      customizeDisabled={columnPrefsSaving}
                    />
                  </tr>
                </thead>
                <tbody>
                  {paginatedRows.map((row) => (
                    <tr key={row._id || row.Symbol}>
                      <td className="text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(row._id)}
                          onChange={(e) => handleSelectOne(row._id, e.target.checked)}
                          disabled={!canDeleteRows || !row._id}
                        />
                      </td>
                      {displayColumns.map((col) => {
                        const colId = col.id;
                        const raw = row[colId] != null ? String(row[colId]) : "";
                        if (colId === "Symbol") {
                          return (
                            <td key={colId}>
                              <strong>{raw || "—"}</strong>
                            </td>
                          );
                        }
                        if (colId === "Equity") {
                          const equityRaw = row.equity != null ? row.equity : row.Equity;
                          return (
                            <td key={colId} className="table-cell-numeric">
                              {formatStockCurrency(equityRaw)}
                            </td>
                          );
                        }
                        if (PCT_COLUMNS.has(colId)) {
                          const { text, tone } = formatStockPercentCell(raw);
                          if (tone === "empty") return <td key={colId}>{text}</td>;
                          return (
                            <td key={colId}>
                              <span className={signedPercentClassName(tone)}>{text}</span>
                            </td>
                          );
                        }
                        return <td key={colId}>{raw || "—"}</td>;
                      })}
                      <td className="text-center" />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </TableViewport>
  );
}

export default StockBuyRecommendation;
